/* The sound arriving at the button.
 *
 * ---------------------------------------------------------------------------
 * WHY A SECOND PICTURE OF THE SAME SIGNAL
 *
 * The strip below is frequency: what is in the sound. This is time: how hard
 * it is hitting, right now. They are the two halves an oscilloscope and a
 * spectrum analyser split between them, and a listener reads them differently
 * -- the strip answers "is that a boat", this answers "is anything there".
 *
 * It radiates outward from the play button because that is where the sound
 * comes from as far as the page is concerned, and because the mark's own
 * motion travels inward for the same reason in reverse: a hydrophone only ever
 * receives. Mirrored left and right, so the button sits in the middle of its
 * own signal rather than beside a chart.
 *
 * ---------------------------------------------------------------------------
 * AND WHY IT IS NOT DECORATION
 *
 * The obvious build -- the one the design reference draws -- is a fixed
 * waveform behind an idle button, because it looks well. This one is fed by
 * the analyser and nothing else, so at rest it is a flat line.
 *
 * That is not pedantry. The whole product is an argument that you cannot tell
 * a quiet sea from a broken sensor by looking, and every component here is
 * built so the two never render the same. A player that draws a lively
 * waveform while nothing is playing has taught the viewer that the picture
 * moves whether or not sound is arriving -- and the next time it moves, they
 * have no reason to believe it. One decorative waveform costs the credibility
 * of every real one on the page.
 *
 * A flat line fading out from the button is what waiting looks like, and it is
 * the same idiom the strip already uses when it collapses to its axis.
 */
import { useEffect, useRef } from 'react'
import { DPR } from '../charts'

/** Where the bars start, measured from the centre. The button is 112 across,
 *  so 56 is its edge; the rest is air, and without it the first bar reads as
 *  part of the ring rather than as signal. */
const INNER = 74
/** Bar every 5px: 2 of ink and 3 of air. Denser reads as a solid block at a
 *  glance, sparser reads as a row of ticks. */
const PITCH = 5
const BAR_W = 2

export function Waveform({ analyser }: { analyser: AnalyserNode | null }) {
  const canvas = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const el = canvas.current
    if (!el) return
    let raf = 0
    let td: Float32Array | null = null
    /* THE BARS ARE A HISTORY, NOT A SNAPSHOT.
     *
     * The first build sliced one analyser buffer -- 32768 samples, about 0.7 s
     * -- into one bar per slice. It is the obvious thing and it produced a
     * barcode: sea noise is broadband and stationary over a fifth of a second,
     * so every slice returned nearly the same peak and the row came out as
     * seventy identical lines. True, and it looked like a loading placeholder.
     *
     * So each bar is now one moment in the recent past, newest at the button
     * and older further out. That makes "a transient enters at the centre and
     * travels outward" literally true rather than a way of describing a
     * gradient, and it gives the row something to show: the sea changes over
     * seconds, which is the timescale a person listening actually perceives. */
    let hist: number[] = []
    /* Peaks accumulate over four frames before one is pushed. At 60 Hz that is
     * a column every 67 ms, so seventy of them hold about five seconds --
     * long enough for a passing boat to be visible as a shape rather than as a
     * flicker, short enough to still read as "now". */
    const EVERY = 4
    let acc = 0
    let accN = 0
    /* Eased floor and ceiling in dB, the same device the strip uses and for
     * the same reason: hydrophone content sits at -56 to -80 dBFS, so a linear
     * map of amplitude would draw every bar at zero height for ever. */
    let floor = -85
    let ceil = -35

    const paint = () => {
      raf = requestAnimationFrame(paint)
      const dpr = DPR()
      const rect = el.getBoundingClientRect()
      const w = Math.max(64, Math.round(rect.width))
      // Read off the element rather than held as a constant here, so the
      // stage's height lives in one place -- `.play-stage` -- instead of in a
      // number that has to be kept equal to a number in the stylesheet.
      const h = Math.max(48, Math.round(rect.height))
      if (el.width !== Math.round(w * dpr) || el.height !== Math.round(h * dpr)) {
        el.width = Math.round(w * dpr)
        el.height = Math.round(h * dpr)
      }
      const c = el.getContext('2d')
      if (!c) return
      c.setTransform(dpr, 0, 0, dpr, 0, 0)
      c.clearRect(0, 0, w, h)

      const mid = h / 2
      const cx = w / 2
      const reach = cx - INNER
      const n = Math.max(0, Math.floor(reach / PITCH))
      if (n <= 0) return

      /* At rest.
       *
       * A hairline out to each edge, fading as it goes. It says the picture is
       * intact and nothing is arriving, which is the true reading, and it is
       * the shape the bars collapse into so the transition is a growth rather
       * than a swap. */
      if (!analyser) {
        for (const dir of [-1, 1]) {
          const g = c.createLinearGradient(cx + dir * INNER, 0, cx + dir * cx, 0)
          g.addColorStop(0, 'rgba(147, 171, 159, 0.30)')
          g.addColorStop(1, 'rgba(147, 171, 159, 0)')
          c.fillStyle = g
          c.fillRect(dir < 0 ? 0 : cx + INNER, mid - 0.5,
                     dir < 0 ? cx - INNER : cx - INNER, 1)
        }
        hist = []
        return
      }

      const len = analyser.fftSize
      if (!td || td.length !== len) td = new Float32Array(len)
      analyser.getFloatTimeDomainData(td)

      let peak = 0
      for (let j = 0; j < len; j++) {
        const a = td[j]! < 0 ? -td[j]! : td[j]!
        if (a > peak) peak = a
      }
      // Maximum across the frames in a column, never a mean. It is the same
      // choice levels.series makes on the server and for the same reason: a
      // short transient averaged away is exactly the thing worth seeing.
      if (peak > acc) acc = peak
      if (++accN >= EVERY) {
        hist.unshift(acc > 0 ? 20 * Math.log10(acc) : -160)
        if (hist.length > n) hist.length = n
        acc = 0
        accN = 0
      }
      if (!hist.length) return

      let loudest = -Infinity
      for (const v of hist) if (v > loudest) loudest = v
      if (Number.isFinite(loudest)) {
        ceil += (Math.max(loudest + 3, floor + 18) - ceil) * 0.06
        floor += ((ceil - 46) - floor) * 0.06
      }

      const span = Math.max(1, ceil - floor)
      /* Deliberately shorter than the button is tall. A waveform that grows
       * past the ring stops reading as sound radiating from the control and
       * starts competing with it for the middle of the card. */
      const maxH = 42

      for (let i = 0; i < hist.length; i++) {
        const norm = Math.max(0, Math.min(1, (hist[i]! - floor) / span))
        const bar = Math.max(1, norm * maxH)
        // Fades outward, so the row has a source rather than two ends -- and
        // here it also means what it looks like, since further out is older.
        const fade = 1 - (i / n) ** 1.35
        c.fillStyle = `rgba(180, 217, 199, ${(0.14 + 0.5 * norm) * fade})`
        const x = INNER + i * PITCH
        c.fillRect(cx + x, mid - bar, BAR_W, bar * 2)
        c.fillRect(cx - x - BAR_W, mid - bar, BAR_W, bar * 2)
      }
    }

    raf = requestAnimationFrame(paint)
    return () => cancelAnimationFrame(raf)
  }, [analyser])

  return <canvas ref={canvas} className="play-wave" aria-hidden="true" />
}
